import { useEffect, useContext, useState } from 'react';
import { useQueryClient } from 'react-query';
import { apigeeEndpoint } from '../apis';
import { LoadingContext } from '../contexts/LoadingContextProvider';
import * as TFF from '@tff/types/TFF';

export type UseInvoluntaryChangesAtcomResponse = {
  involuntaryChangesResponse?: TFF.InvoluntaryTask[];
  setInvoluntaryChangesRequest: (request: InvoluntaryChangesRequest) => void;
  involuntaryChangesRequest?: InvoluntaryChangesRequest;
  errorMessage: string;
  searchDone: boolean | undefined;
};

export type InvoluntaryChangesRequest = {
  searchLabel: string;
  searchValue: string;
};

export const useInvoluntaryChangesAtcom = (): UseInvoluntaryChangesAtcomResponse => {
  const queryClient = useQueryClient();
  const [involuntaryChangesRequest, setInvoluntaryChangesRequest] = useState<InvoluntaryChangesRequest>();
  const [involuntaryChangesResponse, setInvoluntaryChangesResponse] = useState<TFF.InvoluntaryTask[]>([]);
  const { showLoading, closeLoading } = useContext(LoadingContext);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchDone, setSearchDone] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const getInvoluntaryChangesAtcom = async (request: InvoluntaryChangesRequest) => {
      const { searchLabel, searchValue } = request;
      showLoading(`Searching ${searchValue} as ${searchLabel}`);
      await queryClient.fetchQuery(
        ['involuntaryChanges', searchLabel, searchValue],
        async () => {
          let url: string = '/ndc/task/open?clientPnr=&channel=&tfmPnr=';
          if (searchLabel === 'tfm-pnr') {
            url = `/ndc/task/open?clientPnr=&channel=&tfmPnr=${searchValue}`;
          } else if (searchLabel === 'atcom-id') {
            url = `/ndc/task/open?clientPnr=${searchValue}&channel=atcomres&tfmPnr=`;
          } else if (searchLabel === 'booking-id') {
            url = `/ndc/task/open?clientPnr=${searchValue}&channel=aircruiser&tfmPnr=`;
          }
          const response = await apigeeEndpoint.get<TFF.InvoluntaryTask[]>(url);
          setSearchDone(true);
          if ('errorMessage' in response.data) {
            setInvoluntaryChangesResponse([]);
            return [];
          }
          setErrorMessage('');
          setInvoluntaryChangesResponse(response.data);
          return response.data;
        },
        {
          retry: 2,
          staleTime: 1000,
          cacheTime: 50000,
          initialData: () => {
            return queryClient.getQueryData(['involuntaryChanges', searchLabel, searchValue]);
          },
        },
      );
      closeLoading();
    };

    if (involuntaryChangesRequest && involuntaryChangesRequest.searchLabel && involuntaryChangesRequest.searchValue) {
      void getInvoluntaryChangesAtcom(involuntaryChangesRequest);
    }
  }, [involuntaryChangesRequest]);

  return {
    setInvoluntaryChangesRequest,
    involuntaryChangesRequest,
    involuntaryChangesResponse,
    errorMessage,
    searchDone,
  };
};
